import clsx from 'clsx'
import { CSSProperties, useCallback, useId, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { formatDateToPtBr } from '../../../../../../../_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { InfoAlert, ListLoading } from '../../../../../../components'
import { BarrasEmpilhadasChart } from '../../../../../../components/Charts/BarrasEmpilhadasChart'
import type { PontoSerie } from '../../../../../../components/Charts/serieDiariaOpcoes'
import { ChaveIndicadorPerformance } from '../../core/_models'
import {
  Agrupamento,
  agrupamentoPadrao,
  agruparSerie,
  serieRealizacao,
} from '../../core/agrupamento'
import { formatarTaxaPercentual } from '../../core/formatacao'
import { ORDEM_INDICADORES_PERFORMANCE } from '../../core/performance'
import { useParametrosIndicadores } from '../../core/QueryRequestProvider'
import { useQueryResponsePerformance } from '../../core/QueryResponsePerformanceProvider'
import { CardPerformance, formatarRotuloDia } from '../cards/CardPerformance'

const TEMPOS: ChaveIndicadorPerformance[] = [
  'tempoMedioEspera',
  'tempoMedioTriagem',
  'tempoMedioConsulta',
]

const AGRUPAMENTOS: Agrupamento[] = ['dia', 'semana']

const formatarTaxa = (valor: number) => formatarTaxaPercentual(valor) ?? ''
const formatoEixo = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 })
const formatarEixoPercentual = (valor: number) => `${formatoEixo.format(valor)}%`

const BORDA_MARCADA = { '--bs-border-color': 'var(--bs-text-primary)' } as CSSProperties

/** Coluna da grade: `min-width: 0` impede que um gráfico já desenhado force a largura da linha. */
const COLUNA = { minWidth: 0 }

/**
 * Seção da família de performance, consumindo o contrato congelado de
 * `GET /atendimentos/indicadores/performance`. Três blocos (tempos, realização, desfecho);
 * o agrupamento Dia/Semana é calculado aqui, sobre a série diária, sem nova request.
 */
export const SecaoPerformance = () => {
  const { t } = useT()
  const intl = useIntl()
  const idBase = useId()
  const { parametros } = useParametrosIndicadores()
  const { singleResponse: dados, isLoading } = useQueryResponsePerformance()

  // A escolha só vale para o período em que foi feita: trocar o período volta ao padrão.
  const [escolha, setEscolha] = useState<{ chavePeriodo: string; agrupamento: Agrupamento }>()
  const chavePeriodo = dados ? `${dados.dataInicio}|${dados.dataFim}` : ''
  const agrupamento: Agrupamento = !dados
    ? 'dia'
    : escolha?.chavePeriodo === chavePeriodo
    ? escolha.agrupamento
    : agrupamentoPadrao(dados.dataInicio, dados.dataFim)

  const series = useMemo(() => {
    if (!dados) return undefined
    const porIndicador = {} as Record<ChaveIndicadorPerformance, PontoSerie[] | null>
    ORDEM_INDICADORES_PERFORMANCE.forEach((chave) => {
      porIndicador[chave] = agruparSerie(dados.indicadores[chave], agrupamento)
    })
    return {
      porIndicador,
      realizacao: serieRealizacao(
        dados.indicadores.taxaCancelamento,
        dados.indicadores.taxaAbsenteismo,
        agrupamento
      ),
    }
  }, [dados, agrupamento])

  const nomesRealizacao = useMemo(
    () => ({
      realizados: intl.formatMessage({ id: 'indicadores.performance.legenda.realizados' }),
      cancelados: intl.formatMessage({ id: 'indicadores.performance.legenda.cancelados' }),
      faltas: intl.formatMessage({ id: 'indicadores.performance.legenda.faltas' }),
    }),
    [intl]
  )

  const formatarIntervalo = useCallback(
    (inicio: string, fim: string) =>
      intl.formatMessage(
        { id: 'indicadores.performance.grafico.intervalo' },
        { inicio: formatarRotuloDia(inicio), fim: formatarRotuloDia(fim) }
      ),
    [intl]
  )

  if (!parametros) {
    return (
      <InfoAlert variant='info' className='mb-0'>
        {t('indicadores.estado.selecione_empresa')}
      </InfoAlert>
    )
  }

  const slaNaoConfigurado = dados?.slaLimiteMinutos === null
  const semanal = agrupamento === 'semana'
  const ids = {
    tempos: `${idBase}-tempos`,
    realizacao: `${idBase}-realizacao`,
    graficoRealizacao: `${idBase}-grafico-realizacao`,
    desfecho: `${idBase}-desfecho`,
  }

  const card = (
    chave: ChaveIndicadorPerformance,
    extra: Partial<Parameters<typeof CardPerformance>[0]> = {}
  ) =>
    dados &&
    series && (
      <CardPerformance
        chave={chave}
        indicador={dados.indicadores[chave]}
        comparacaoMesCivil={dados.comparacaoMesCivil}
        variante='grafico'
        pontos={series.porIndicador[chave]}
        agrupamento={agrupamento}
        {...extra}
      />
    )

  const pontosRealizacao = series?.realizacao ?? []
  const temRealizacao = pontosRealizacao.some((p) => p.realizados !== null && p.n > 0)
  const temColunaSemDado = pontosRealizacao.some((p) => p.realizados === null || p.n <= 0)
  const legendaRealizacao: { nome: string; cor: string; semDado?: boolean }[] = [
    { nome: nomesRealizacao.realizados, cor: '--bs-gray-300' },
    { nome: nomesRealizacao.cancelados, cor: '--app-chart-series' },
    { nome: nomesRealizacao.faltas, cor: '--app-chart-series-alt' },
    ...(temColunaSemDado
      ? [
          {
            nome: t('indicadores.performance.legenda.sem_dados'),
            cor: '--bs-gray-700',
            semDado: true,
          },
        ]
      : []),
  ]

  return (
    <div aria-live='polite' aria-busy={isLoading}>
      {isLoading && <ListLoading />}
      {isLoading && !dados && (
        <span className='visually-hidden'>{t('indicadores.estado.carregando')}</span>
      )}

      {dados && series && (
        <div className={clsx(isLoading && 'opacity-50')}>
          <h2 className='fs-3 fw-bold text-gray-900 mb-1'>{t('indicadores.performance.titulo')}</h2>
          <p className='fs-6 text-gray-700 mb-2'>{t('indicadores.performance.subtitulo')}</p>
          <div className='d-flex flex-wrap justify-content-between align-items-center gap-3 mb-6'>
            <p className='fs-7 text-gray-700 mb-0'>
              <span>
                {t('indicadores.periodo', {
                  inicio: formatDateToPtBr(dados.dataInicio),
                  fim: formatDateToPtBr(dados.dataFim),
                })}
              </span>
              <span aria-hidden='true'> · </span>
              <span>
                {t('indicadores.performance.periodo_anterior', {
                  inicio: formatDateToPtBr(dados.periodoAnterior.dataInicio),
                  fim: formatDateToPtBr(dados.periodoAnterior.dataFim),
                })}
              </span>
            </p>

            <div
              className='d-inline-flex gap-2'
              role='group'
              aria-label={t('indicadores.performance.agrupamento.rotulo')}
            >
              {AGRUPAMENTOS.map((opcao) => {
                const ativo = opcao === agrupamento
                return (
                  <button
                    key={opcao}
                    type='button'
                    aria-pressed={ativo}
                    className={
                      ativo
                        ? 'btn btn-sm bg-light-primary text-primary border fw-bold'
                        : 'btn btn-sm btn-color-gray-700 btn-active-color-primary border border-gray-300'
                    }
                    // Estado marcado também pela borda forte, pelo peso e pelo ícone, não só pelo fundo.
                    // A cor entra pela variável que o `.border` lê: um `border-color` inline perde para o
                    // `!important` dele, e o `.border-primary` some no escuro.
                    style={ativo ? BORDA_MARCADA : undefined}
                    onClick={() => setEscolha({ chavePeriodo, agrupamento: opcao })}
                  >
                    {ativo && <i className='bi bi-check-lg text-primary me-1' aria-hidden='true' />}
                    {t(`indicadores.performance.agrupamento.${opcao}`)}
                  </button>
                )
              })}
            </div>
          </div>

          <section aria-labelledby={ids.tempos} className='mb-8'>
            <div className='d-flex flex-wrap align-items-baseline column-gap-4 mb-4'>
              <h3 id={ids.tempos} className='fs-4 fw-bold text-gray-900 mb-0'>
                {t('indicadores.performance.blocos.tempos')}
              </h3>
              <p className='fs-7 text-gray-700 mb-0'>
                {t('indicadores.performance.blocos.tempos_nota')}
              </p>
            </div>
            <div className='row g-5'>
              {TEMPOS.map((chave) => (
                <div key={chave} className='col-12 col-lg-4' style={COLUNA}>
                  {card(
                    chave,
                    chave === 'tempoMedioTriagem'
                      ? {
                          extra: t('indicadores.performance.tempo_triagem.afericao_unica', {
                            n: dados.atendimentosComAfericaoUnica,
                          }),
                        }
                      : {}
                  )}
                </div>
              ))}
            </div>
          </section>

          <section aria-labelledby={ids.realizacao} className='mb-8'>
            <h3 id={ids.realizacao} className='fs-4 fw-bold text-gray-900 mb-4'>
              {t('indicadores.performance.blocos.realizacao')}
            </h3>
            <div className='row g-5'>
              <div className='col-12 col-xl-8' style={COLUNA}>
                <div className='card card-flush h-100 border border-gray-300'>
                  <div className='card-body p-5'>
                    <div className='d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-2'>
                      <h4 id={ids.graficoRealizacao} className='fs-6 fw-bold text-gray-900 mb-0'>
                        {t(
                          semanal
                            ? 'indicadores.performance.realizacao.titulo_semana'
                            : 'indicadores.performance.realizacao.titulo_dia'
                        )}
                      </h4>
                      <span className='fs-7 text-gray-700'>
                        {t(
                          semanal
                            ? 'indicadores.performance.realizacao.unidade_semana'
                            : 'indicadores.performance.realizacao.unidade_dia'
                        )}
                      </span>
                    </div>
                    {temRealizacao && (
                      <ul className='list-unstyled d-flex flex-wrap gap-4 fs-7 text-gray-700 mb-3'>
                        {legendaRealizacao.map((item) => (
                          <li key={item.cor} className='d-flex align-items-center'>
                            <span
                              className={`d-inline-block me-2${item.semDado ? '' : ' rounded-1'}`}
                              style={
                                item.semDado
                                  ? { width: 8, height: 8, background: `var(${item.cor})` }
                                  : { width: 12, height: 12, background: `var(${item.cor})` }
                              }
                              aria-hidden='true'
                            />
                            {item.nome}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className='overflow-hidden'>
                      <BarrasEmpilhadasChart
                        pontos={series.realizacao}
                        nomes={nomesRealizacao}
                        formatarPercentual={formatarTaxa}
                        formatarEixoY={formatarEixoPercentual}
                        formatarRotuloDia={formatarRotuloDia}
                        formatarIntervalo={formatarIntervalo}
                        textoSemDados={t('indicadores.card.sem_dados')}
                        tabela={{
                          legenda: t(
                            semanal
                              ? 'indicadores.performance.realizacao.titulo_semana'
                              : 'indicadores.performance.realizacao.titulo_dia'
                          ),
                          colunaData: t(
                            semanal
                              ? 'indicadores.performance.grafico.coluna_semana'
                              : 'indicadores.performance.grafico.coluna_data'
                          ),
                          colunaN: t('indicadores.performance.grafico.coluna_lancados'),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-xl-4 d-flex flex-column gap-5' style={COLUNA}>
                <div className='flex-grow-1'>
                  {card('taxaCancelamento', {
                    variante: 'taxa',
                    corMarcador: '--app-chart-series',
                  })}
                </div>
                <div className='flex-grow-1'>
                  {card('taxaAbsenteismo', {
                    variante: 'taxa',
                    corMarcador: '--app-chart-series-alt',
                  })}
                </div>
              </div>
            </div>
          </section>

          <section aria-labelledby={ids.desfecho}>
            <h3 id={ids.desfecho} className='fs-4 fw-bold text-gray-900 mb-4'>
              {t('indicadores.performance.blocos.desfecho')}
            </h3>
            <div className='row g-5'>
              <div className='col-12 col-lg-6' style={COLUNA}>
                {card('taxaResolutividade', {
                  selo: t('indicadores.performance.taxa_resolutividade.aproximado'),
                })}
              </div>
              <div className='col-12 col-lg-6' style={COLUNA}>
                {card('slaCumprido', {
                  slaNaoConfigurado,
                  selo:
                    dados.slaLimiteMinutos === null
                      ? undefined
                      : t('indicadores.performance.sla_cumprido.selo', {
                          x: dados.slaLimiteMinutos,
                        }),
                })}
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
